import { HStack, Skeleton, useDisclosure } from '@chakra-ui/react';
import {
  GetDisclosureRequirementGroupsDocument_,
  GetDisclosureRequirementsOwnerDocument_,
  GetDrOwnerDocument_,
  GetMfDisclosureRequirementGroupsDocument_,
  GetMfDrOwnerDocument_,
  ShortUser,
  useGetDrOwnerQuery,
  useGetMfDrOwnerQuery,
  useUpsertDrOwnerMutation,
  useUpsertMfDrOwnerMutation,
} from 'models';
import { UserAvatar } from 'Organisms';
import { Typography } from 'Tokens';
import { DREnums } from '../../../Requirement';
import { MetricInfoCard } from '../../../StandardPage/StandardPageComponents/DisclosureRequirementHeader';
import {
  BusinessUnitDrProgress,
  GroupDrProgress,
  CompanyDrProgress,
} from '../../../StandardPage/StandardPageComponents/DisclosureRequirementsTable';
import { IconButton } from 'Atoms';
import { EditIcon } from 'Tokens/Icons/Data';
import { useMemo, useState } from 'react';
import { ResponsibleSelector, ToolbarToast } from 'Molecules/TaskToolbar';
import { mfStandardRefs } from 'containers/Esrs/utils';
import { getStandardDatapoints } from '../../../StandardPage/Standards.hooks';
import { useHandleTaskUpdate } from '../ProcessMetrics/MetricsHelperFunctions';
import { EmptyOwner } from 'Atoms/EmptyOwner';
import { Menu } from 'Molecules/Menu';

export const DrInfoHeader = ({
  isBusinessUnit,
  isGroup,
  drReference,
  drType,
  standardRef,
  reportingUnitId,
  materialStandardId,
  framework,
  esrsAssessmentId,
  parentAssessmentId,
  projectLeader,
  companyReportingUnitId,
  requiredDrMetrics,
  parentEsrsAssessmentId,
}: {
  isBusinessUnit: boolean;
  isGroup: boolean;
  drReference: string;
  materialStandardId?: string;
  drType: string;
  standardRef: string;
  reportingUnitId?: string;
  framework: string;
  esrsAssessmentId?: string;
  parentAssessmentId?: string;
  projectLeader?: ShortUser;
  companyReportingUnitId?: string;
  requiredDrMetrics?: string[];
  parentEsrsAssessmentId?: string;
}) => {
  const [isOwnerHovered, setIsOwnerHovered] = useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [toastVisible, setToastVisible] = useState(false);
  const [toastText, setToastText] = useState('');
  const [isToastLoading, setIsToastLoading] = useState(false);
  const [responsibleFor, setResponsibleFor] = useState('all');
  const isMFStandard = mfStandardRefs.includes(standardRef);
  const currentReportingUnitId = useMemo(
    () => (isBusinessUnit ? reportingUnitId : companyReportingUnitId),
    [isBusinessUnit, reportingUnitId, companyReportingUnitId]
  );
  const { data: drOwner, loading } = useGetDrOwnerQuery({
    variables: {
      drRef: drReference,
      materialStandardId,
      reportingUnitId: currentReportingUnitId,
    },
    skip: !materialStandardId || isMFStandard || !currentReportingUnitId,
  });
  const { data: mfDrOwner, loading: mfDrLoading } = useGetMfDrOwnerQuery({
    variables: {
      mfDrRef: drReference,
      materialStandardId,
      reportingUnitId: currentReportingUnitId,
    },
    skip: !materialStandardId || !isMFStandard || !currentReportingUnitId,
  });
  const [upsertDrOwner] = useUpsertDrOwnerMutation();
  const [upsertMfDrOwner] = useUpsertMfDrOwnerMutation();

  const allDpRows = getStandardDatapoints(
    materialStandardId ?? '',
    parentAssessmentId ?? '',
    requiredDrMetrics
  ).metrics;

  const handleTaskUpdate = useHandleTaskUpdate(
    allDpRows,
    projectLeader?.id,
    companyReportingUnitId
  );

  const handleToast = (ownerName?: string) => {
    setIsToastLoading(false);
    setToastVisible(false);
    setToastText(`Assigned ${ownerName} as responsible`);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 3000);
  };

  const handleUpdateOwner = (ownerId: string, ownerName?: string) => {
    setIsToastLoading(true);
    setToastText(`Assigning responsible...`);
    setToastVisible(true);
    if (isMFStandard) {
      upsertMfDrOwner({
        variables: {
          mfDrRef: drReference,
          materialStandardId,
          ownerId,
          reportingUnitId: currentReportingUnitId,
        },
        refetchQueries: [
          GetMfDrOwnerDocument_,
          GetDisclosureRequirementGroupsDocument_,
          GetMfDisclosureRequirementGroupsDocument_,
          GetDisclosureRequirementsOwnerDocument_,
        ],
      }).then(() => {
        if (responsibleFor !== 'all') {
          handleToast(ownerName);
        }
      });
    } else {
      upsertDrOwner({
        variables: {
          drRef: drReference,
          materialStandardId,
          ownerId,
          reportingUnitId: currentReportingUnitId,
        },
        refetchQueries: [
          GetDrOwnerDocument_,
          GetDisclosureRequirementGroupsDocument_,
          GetMfDisclosureRequirementGroupsDocument_,
          GetDisclosureRequirementsOwnerDocument_,
        ],
      }).then(() => {
        if (responsibleFor !== 'all') {
          handleToast(ownerName);
        }
      });
    }
    if (responsibleFor === 'all') {
      handleTaskUpdate('Responsible', {
        responsible: ownerId,
      }).then(() => {
        handleToast(ownerName);
      });
    }
  };
  const owner = useMemo(
    () =>
      drOwner?.DisclosureRequirementOwner?.[0]?.owner ??
      mfDrOwner?.DisclosureRequirementOwner?.[0]?.owner,
    [drOwner, mfDrOwner]
  );
  return (
    <>
      <ToolbarToast text={toastText} visible={toastVisible} isLoading={isToastLoading} />

      <HStack w="100%" gap="8px">
        <MetricInfoCard
          title="Data collection progress"
          content={
            isBusinessUnit ? (
              <BusinessUnitDrProgress
                drRef={drReference ?? ''}
                assessmentId={esrsAssessmentId ?? ''}
                isActionOrTarget={drType === DREnums.action || drType === DREnums.target}
                standardRef={standardRef}
                parentAssessmentId={parentEsrsAssessmentId}
                reportingUnitId={reportingUnitId ?? ''}
                frameworkRef={framework}
                size="28px"
                textVariant="h3"
              />
            ) : isGroup ? (
              <GroupDrProgress
                drRef={drReference ?? ''}
                assessmentId={esrsAssessmentId ?? ''}
                isActionOrTarget={drType === DREnums.action || drType === DREnums.target}
                standardRef={standardRef}
                frameworkRef={framework}
                size="28px"
                textVariant="h3"
              />
            ) : (
              <CompanyDrProgress
                drRef={drReference ?? ''}
                assessmentId={esrsAssessmentId ?? ''}
                isActionOrTarget={drType === DREnums.action || drType === DREnums.target}
                standardRef={standardRef}
                parentAssessmentId={parentEsrsAssessmentId}
                frameworkRef={framework}
                size="28px"
                textVariant="h3"
              />
            )
          }
        />
        <MetricInfoCard
          title="Main responsible"
          onMouseEnter={() => setIsOwnerHovered(true)}
          onMouseLeave={() => setIsOwnerHovered(false)}
          content={
            <Skeleton isLoaded={!loading && !mfDrLoading}>
              <HStack spacing="8px">
                {owner ? (
                  <UserAvatar user={owner} size="sm" boxSize="28px" border="none" />
                ) : (
                  <EmptyOwner />
                )}
                <Typography variant="h3">{owner ? owner?.displayName : 'Assign'}</Typography>
                {isOwnerHovered && (
                  <Menu
                    menuButton={
                      <IconButton
                        aria-label="edit-dr-owner"
                        icon={<EditIcon />}
                        borderRadius="50%"
                        variant="ghost"
                        size="sm"
                      />
                    }
                    sections={[
                      {
                        title: 'Assign responsability to',
                        actions: [
                          {
                            id: 'all',
                            title: 'Disclosure requirement and all data points',
                            onClick: () => {
                              setResponsibleFor('all');
                              onOpen();
                            },
                          },
                          {
                            id: 'all',
                            title: 'Disclosure requirement only',
                            onClick: () => {
                              setResponsibleFor('dr');
                              onOpen();
                            },
                          },
                        ],
                      },
                    ]}
                    placement="bottom"
                  />
                )}
              </HStack>
            </Skeleton>
          }
        />
      </HStack>
      <ResponsibleSelector
        isOpen={isOpen}
        onClose={onClose}
        onSelect={handleUpdateOwner}
        isStandalone
      />
    </>
  );
};
